import { useMemo, useState } from "react";
import { ArrowDownAZ, DollarSign, Search, Star, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import { useCompanies } from "@/hooks/api";
import { useToggleState } from "@/hooks/useToggleState";
import { cn } from "@/lib/utils";
import { formatPct, formatPrice } from "@/lib/format";
import type { CompanyListItemOut } from "@/api/client";

type NameOrder = null | "asc" | "desc";
type RevenueOrder = null | "desc" | "asc";
type PredictionHorizon = null | "d2" | "week" | "month";
type StarMode = "on" | "off" | "only";

export function CompaniesSidebar({
  selected,
  onSelect,
}: {
  selected: string | null;
  onSelect: (symbol: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [starMode, cycleStar] = useToggleState<StarMode>(["on", "off", "only"]);
  const [nameOrder, cycleName] = useToggleState<NameOrder>([null, "asc", "desc"]);
  const [revenueOrder, cycleRevenue] = useToggleState<RevenueOrder>([null, "desc", "asc"]);
  const [predictionOrder, cyclePrediction] = useToggleState<PredictionHorizon>([
    null,
    "d2",
    "week",
    "month",
  ]);

  const { data, isLoading } = useCompanies(query || undefined);

  const list = useMemo(() => {
    const rows = data ?? [];
    const arr = [...rows];
    if (revenueOrder) {
      arr.sort((a, b) => {
        const av = Number(a.revenue_ttm_usd ?? 0);
        const bv = Number(b.revenue_ttm_usd ?? 0);
        return revenueOrder === "desc" ? bv - av : av - bv;
      });
    }
    if (predictionOrder) {
      // No predictions endpoint per-row yet; placeholder sort uses change_pct
      // so the chip still does something useful with empty ML data.
      arr.sort((a, b) => (b.change_pct ?? 0) - (a.change_pct ?? 0));
    }
    if (nameOrder) {
      arr.sort((a, b) => {
        const al = (a.primary_symbol ?? a.name).toUpperCase();
        const bl = (b.primary_symbol ?? b.name).toUpperCase();
        return nameOrder === "asc" ? al.localeCompare(bl) : bl.localeCompare(al);
      });
    }
    if (starMode === "only") {
      return arr.filter((c) => c.starred);
    }
    if (starMode === "on") {
      // Stable partition: arr is already sorted by the active criterion, so each
      // group (starred / non-starred) stays sorted.
      const starred = arr.filter((c) => c.starred);
      const rest = arr.filter((c) => !c.starred);
      return [...starred, ...rest];
    }
    return arr;
  }, [data, revenueOrder, predictionOrder, nameOrder, starMode]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b border-border space-y-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ticker or name..."
            className="pl-7 h-7 text-[12px]"
          />
        </div>
        <div className="flex items-center gap-1">
          <FilterChip
            active={starMode !== "off"}
            state={starMode === "on" ? "★" : starMode === "only" ? "★!" : ""}
            onClick={cycleStar}
            title="Star sorting: on / off / only favorites"
          >
            <Star className="h-3 w-3" />
          </FilterChip>
          <FilterChip
            active={!!nameOrder}
            state={nameOrder === "asc" ? "A↓Z" : nameOrder === "desc" ? "Z↓A" : ""}
            onClick={cycleName}
            title="Sort by ticker"
          >
            <ArrowDownAZ className="h-3 w-3" />
          </FilterChip>
          <FilterChip
            active={!!revenueOrder}
            state={revenueOrder === "desc" ? "↓" : revenueOrder === "asc" ? "↑" : ""}
            onClick={cycleRevenue}
            title="Sort by revenue"
          >
            <DollarSign className="h-3 w-3" />
          </FilterChip>
          <FilterChip
            active={!!predictionOrder}
            state={
              predictionOrder === "d2"
                ? "2D"
                : predictionOrder === "week"
                  ? "1W"
                  : predictionOrder === "month"
                    ? "1M"
                    : ""
            }
            onClick={cyclePrediction}
            title="Sort by predicted upside"
          >
            <TrendingUp className="h-3 w-3" />
          </FilterChip>
          <div className="ml-auto text-[10px] text-muted-foreground font-mono">
            {isLoading ? "…" : list.length}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
        )}
        {!isLoading && list.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
            No companies in the database yet.
          </div>
        )}
        {list.map((c) => (
          <CompanyRow
            key={c.id}
            company={c}
            selected={selected === c.primary_symbol}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  state,
  onClick,
  title,
  children,
}: {
  active: boolean;
  state?: string;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Toggle
      pressed={active}
      onPressedChange={onClick}
      title={title}
      size="sm"
      className="h-6 px-1.5 rounded-[4px] border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
    >
      {children}
      {state && <span className="text-[9px] font-mono opacity-80 ml-0.5">{state}</span>}
    </Toggle>
  );
}

function CompanyRow({
  company,
  selected,
  onSelect,
}: {
  company: CompanyListItemOut;
  selected: boolean;
  onSelect: (symbol: string) => void;
}) {
  const up = (company.change_pct ?? 0) >= 0;
  const symbol = company.primary_symbol ?? "—";
  return (
    <button
      onClick={() => company.primary_symbol && onSelect(company.primary_symbol)}
      disabled={!company.primary_symbol}
      className={cn(
        "w-full text-left px-2 py-1.5 border-b border-border/60 hover:bg-accent/60 transition-colors block disabled:opacity-50 min-h-[44px] md:min-h-0",
        selected && "bg-accent",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-1 min-w-0">
          {company.starred && (
            <Star className="h-2.5 w-2.5 text-amber-400 fill-amber-400 shrink-0 self-center" />
          )}
          <div className="font-mono font-semibold text-[12px] whitespace-nowrap">{symbol}</div>
        </div>
        <div className="font-mono text-[11px] tabular-nums whitespace-nowrap">
          {formatPrice(company.last_price)}
        </div>
      </div>
      <div className="flex items-baseline justify-between gap-2 mt-0.5 min-w-0">
        <div className="text-[10px] text-muted-foreground truncate min-w-0">{company.name}</div>
        <div
          className={cn(
            "font-mono text-[10px] tabular-nums whitespace-nowrap shrink-0",
            company.change_pct === null
              ? "text-muted-foreground"
              : up
                ? "text-[hsl(var(--chart-up))]"
                : "text-[hsl(var(--chart-down))]",
          )}
        >
          {formatPct(company.change_pct)}
        </div>
      </div>
    </button>
  );
}
